import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react';

import { Stack, HStack, Box, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { getIconColor, StackedHeader } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import {
  publishedAtOptions,
  Select,
  SelectHistoricalOptions,
} from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { CVEAdditionalDetailsModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEAdditionalDetailsModal';
import { CVEReferencesModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEReferencesModal';
import {
  exploitableOptions,
  packageTypeOptions,
  patchAvailableOptions,
  statusOptions,
  vulnerabilitySeverityOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { CvesById } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/Package/CvesById';
import { PackagesHostsDrawerDetails } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/Package/PackagesHostsDrawerDetails';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  row: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
  selectedTab: string;
  isResolved?: boolean;
}

export const CVEsByPackage: FC<Props> = props => {
  const { row, isOpen, onClose, selectedTab } = props;
  const dispatch = useDispatch();
  const { vulnerabilityType } = useSelector(selectVulnerabilities);

  const cveInfoDisclosure = useDisclosure();

  const ref = useRef(document.querySelector('.portal-container'));

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      historicalTime: publishedAtOptions[0],
      isPatchable: patchAvailableOptions[0],
      sortByField: {
        sortField: '',
        sortOrder: '',
      },
      showMoreLinks: false,
      status: statusOptions[vulnerabilityType === 'open' ? 0 : 1],
      severity: vulnerabilitySeverityOptions[0],
      hasPublicExploit: exploitableOptions[0],
      searchTerm: '',
      finalSearchTerm: '',
    },
  );

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  const styles = {
    menu: provided => ({
      ...provided,
      width: 'max-content',
      minWidth: '100%',
      right: 0,
    }),
  };

  useEffect(() => {
    dispatch(actions.resetFilters());
  }, [dispatch]);

  const header = (
    <StackedHeader
      upper={row?.package_name}
      lower={row?.installed_version}
      type={row?.package_category_name ?? IconTypes.Packages}
      color={getIconColor(row?.package_category_name)}
      useCustomColor
    />
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={header}
        body={
          <Stack w="full" h="full" spacing={5}>
            <PackagesHostsDrawerDetails row={row} />

            <BorderedBox flex={1} w="full" h="full" minH="400px">
              <Stack h="full" spacing={5} w="full">
                <HStack w="full">
                  <Box w={72}>
                    <SearchBox
                      value={state.searchTerm}
                      onChange={s => updateValue('searchTerm', s)}
                      onSearch={() =>
                        updateValue('finalSearchTerm', state.searchTerm)
                      }
                      placeholder="Search"
                      onClickClose={() =>
                        updateState({ searchTerm: '', finalSearchTerm: '' })
                      }
                    />
                  </Box>
                  <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                  <Box zIndex={900}>
                    <Select
                      options={vulnerabilitySeverityOptions}
                      value={state?.severity}
                      onChange={s => updateValue('severity', s)}
                      showIconInValueContainer
                      styles={styles}
                      menuPortalTarget={document.body}
                      leftMessage="Severity"
                      isAFilter
                      showUndefinedValueLabel={false}
                    />
                  </Box>
                  <Box zIndex={900}>
                    <Select
                      options={exploitableOptions}
                      value={state?.hasPublicExploit}
                      onChange={s => updateValue('hasPublicExploit', s)}
                      showIconInValueContainer
                      styles={styles}
                      menuPortalTarget={document.body}
                      leftMessage="Has Public Exploit"
                      isAFilter
                      showUndefinedValueLabel={false}
                    />
                  </Box>
                  <Box>
                    <SelectHistoricalOptions
                      historicalTime={state.historicalTime}
                      setHistoricalTime={s => updateValue('historicalTime', s)}
                      isAFilter
                      leftMessage="Timeline"
                      width="full"
                    />
                  </Box>
                </HStack>
                <CvesById
                  currentRecord={row}
                  severity={state.severity}
                  hasPublicExploit={state.hasPublicExploit}
                  searchTerm={state.finalSearchTerm}
                />
              </Stack>
            </BorderedBox>
          </Stack>
        }
        closeButton
        styles={{
          content: { maxW: '1600px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
      />
      {state.showMoreLinks && (
        <CVEReferencesModal
          isOpen={state.showMoreLinks}
          onClose={() => updateValue('showMoreLinks', false)}
          cveDetails={row}
        />
      )}
      {cveInfoDisclosure.isOpen && (
        <CVEAdditionalDetailsModal
          cveDetails={row}
          isOpen={cveInfoDisclosure.isOpen}
          onClose={cveInfoDisclosure.onClose}
        />
      )}
    </>
  );
};
